import React, { useEffect, useRef, useState } from "react";
import "./article.scss";
import { graphql } from "gatsby";
import parse from "html-react-parser";

// We're using Gutenberg so we need the block styles
// these are copied into this project due to a conflict in the postCSS
// version used by the Gatsby and @wordpress packages that causes build
// failures.
// @todo update this once @wordpress upgrades their postcss version
import "../../css/@wordpress/block-library/build-style/style.css";
import "../../css/@wordpress/block-library/build-style/theme.css";
import Layout from "../../components/layout/layout";
import Seo from "../../components/seo";
import PostCardList from "../../components/postCardList/postCardList";
import Breadcrumb from "../../components/breadcrumb/breadcrumb";

import { OG_IMG } from "../../constants/fallback";
import { BRAND_LOGO_URL } from "../../constants/calculators";
import Pill from "../../components/common/pill/pill";
import ArticleTitle from "../../components/common/article-title/articleTitle";
import ArticleInfo from "../../components/common/article-info/articleInfo";
import { PILL_TYPE } from "../../components/common/pill/pill.constant";
import ArticleTags from "../../components/common/article-tags/articleTags";
import { ARTICLE_INFO_TYPE } from "../../components/common/article-info/articleInfo.constants";
import FeaturedImage from "../../components/common/featured-image/featuredImage";
import {
  CARD_ORIENTATION,
  IMG_POSITION,
} from "../../components/common/post-card/postCard.constant";
import PopularCategories from "../../components/common/popular-categories/popularCategories";
import { POPULAR_CATEGORIES_TITLE } from "../../constants/articlesDefaults";
import {
  SIMILAR_ARTICLES_TITLE,
  BUZZ_CAROUSEL_CLASS_NAME,
  BUZZ_SHARE_MARKET_SHARE_TITLE,
  ARTICLE_BANNER_IMG_FALLBACK,
  ARTICLE_BANNER_IMG_FALLBACK_ALT,
  ARTICLE_BANNER_IMG_FALLBACK_MOBILE,
  ARTICLE_DATA,
  ARTICLE_CATEGORY_PILL_FLOW,
  ARTICLE_SHARE_WIDGET_FLOW,
  ARTICLE_RELATED_TAGS_FLOW,
  RELATED_ARTICLES_FLOW,
  POPULAR_CATEGORIES_FLOW,
} from "./article.constants";
import ShareWidget from "../../components/common/share-widget/shareWidget";
import {
  getShareUrl,
  setupArticleImgCarousels,
} from "./article.helper";
import { isMobile } from "../../utils/breakPoints";
import {
  getImgUrl,
  IMG_VARIANTS,
} from "../../constants/imgVariants";
import { triggerBuzzPageLoadEvent } from "../../utils/garfieldHelpers";
import {
  EVENT_NAMES,
  EVENT_SOURCE,
  SECTION_IDS,
} from "../../constants/garfieldEvents";

import { getBlogPostUri } from "../../utils/buzzHelper";

const Article = ({
  data: { post, popularCategories, allPosts },
}) => {
  const [articleBannerSrc, setArticleBannerSrc] = useState(
    getImgUrl(post?.slug, IMG_VARIANTS.WEB_BANNER)
  );
  const contentRef = useRef();

  const posts = allPosts.nodes.filter(
    currentPost => currentPost.title !== post.title
  );

  const popularCategoriesList =
    popularCategories?.nodes[0]?.wpChildren.nodes || [];

  const featuredImage = post.featuredImage
    ? {
        fluid:
          post.featuredImage?.node?.localFile
            ?.childImageSharp?.fluid,
        alt: post.featuredImage?.node?.alt || "",
      }
    : null;

  const postMetaTitle =
    post.seo?.opengraphTitle || post.title;

  const postMetaDescription =
    post.seo?.opengraphDescription || post.excerpt;

  const postFeaturedImgUrl = featuredImage
    ? `https://share.market/blog${featuredImage.fluid?.src}`
    : OG_IMG;

  const postSeoSchemas = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://share.market/buzz${getBlogPostUri(
        post
      )}`,
    },
    headline: postMetaTitle,
    description: postMetaDescription,
    image: postFeaturedImgUrl,
    author: {
      "@type": "Organization",
      name: "Share.Market",
      url: "https://share.market/",
    },
    publisher: {
      "@type": "Organization",
      name: "Share.Market",
      logo: {
        "@type": "ImageObject",
        url: BRAND_LOGO_URL,
      },
    },
  };

  const postCategory = post?.categories?.nodes
    ? post?.categories?.nodes[0]
    : undefined;

  const breadcrumbLinks = [
    {
      uri: "/",
      name: "Buzz",
    },
    {
      uri: post?.categories?.nodes[0].uri,
      name: post?.categories?.nodes[0].name,
    },
    {
      uri: "",
      name: post.title,
    },
  ];

  const ArticleBanner = () => (
    <>
      <div className="article-banner">
        <FeaturedImage
          imgSrc={articleBannerSrc}
          postFeaturedImg={featuredImage}
          postTitle={post?.title}
          fallBackImg={ARTICLE_BANNER_IMG_FALLBACK}
          fallBackImgMobile={
            ARTICLE_BANNER_IMG_FALLBACK_MOBILE
          }
          fallBackImgAlt={
            post?.title || ARTICLE_BANNER_IMG_FALLBACK_ALT
          }
          fallBackImgCustomClassName={"fallback-img"}
        />
      </div>
      <Breadcrumb
        breadcrumbLinks={breadcrumbLinks}
        className={"post-breadcrumb"}
      />
    </>
  );

  useEffect(() => {
    if (!isMobile()) {
      const carouselDivs = document.getElementsByClassName(
        BUZZ_CAROUSEL_CLASS_NAME
      );

      const contentRight = contentRef?.current?.getBoundingClientRect()
        ?.right;

      setupArticleImgCarousels(carouselDivs, contentRight);
    } else {
      setArticleBannerSrc(
        getImgUrl(post?.slug, IMG_VARIANTS.MOBILE_BANNER)
      );
    }
    triggerBuzzPageLoadEvent(
      EVENT_NAMES.ARTICLE_PAGE_LOADED,
      {
        [ARTICLE_DATA.TITLE]: post?.title,
        [ARTICLE_DATA.ARTICLE_SLUG]: post?.slug,
        [ARTICLE_DATA.ARTICLE_CATEGORY]:
          post?.categories?.nodes[0].name,
      }
    );
  }, []);

  return (
    <>
      <Layout articleBanner={<ArticleBanner />}>
        <Seo
          title={postMetaTitle}
          description={postMetaDescription}
          pageURI={`/${post?.categories?.nodes[0].slug}${post.uri}`}
          image={postFeaturedImgUrl}
          meta={[
            {
              name: "robots",
              content: `${
                post?.seo?.metaRobotsNofollow || "follow"
              }, ${
                post?.seo?.metaRobotsNoindex || "index"
              }`,
            },
          ]}
        />
        <div className="blog-post">
          <div className="content-left" ref={contentRef}>
            <article
              className="post"
              itemScope
              itemType="http://schema.org/Article"
            >
              <div className="post-category">
                {postCategory && (
                  <Pill
                    data={postCategory}
                    pillType={PILL_TYPE.CATEGORY}
                    sectionId={SECTION_IDS.CATEGORY_PILL}
                    source={EVENT_SOURCE.ARTICLE}
                    flow={ARTICLE_CATEGORY_PILL_FLOW}
                  />
                )}
              </div>
              <ArticleTitle title={post.title} />
              <ArticleInfo
                articleAuthor={post?.author}
                publishDate={post?.date}
                readingTime={post?.seo?.readingTime}
                articleInfoType={ARTICLE_INFO_TYPE.TITLE}
              />
              <ShareWidget
                url={getShareUrl(
                  post?.categories?.nodes[0].slug,
                  post?.slug
                )}
                title={BUZZ_SHARE_MARKET_SHARE_TITLE}
                text={post?.title}
                sectionId={SECTION_IDS.SHARE_WIDGET_TOP}
                source={EVENT_SOURCE.ARTICLE}
                flow={ARTICLE_SHARE_WIDGET_FLOW}
              />

              {!!post.content && (
                <section
                  itemProp="articleBody"
                  className="post-content"
                >
                  {parse(post?.content || " ")}
                </section>
              )}
              {post?.tags?.nodes?.length !== 0 && (
                <ArticleTags
                  tags={post?.tags?.nodes}
                  sectionId={SECTION_IDS.RELATED_TAGS}
                  source={EVENT_SOURCE.ARTICLE}
                  flow={ARTICLE_RELATED_TAGS_FLOW}
                />
              )}
              <ShareWidget
                url={getShareUrl(
                  post?.categories?.nodes[0].slug,
                  post?.slug
                )}
                title={BUZZ_SHARE_MARKET_SHARE_TITLE}
                text={post?.title}
                sectionId={SECTION_IDS.SHARE_WIDGET_BOTTOM}
                source={EVENT_SOURCE.ARTICLE}
                flow={ARTICLE_SHARE_WIDGET_FLOW}
              />
            </article>
          </div>
        </div>
        {posts?.length > 0 ? (
          <div className="related-articles">
            <div>
              <div className="similar-posts-title rajdhani-font">
                {SIMILAR_ARTICLES_TITLE}
              </div>
              <PostCardList
                posts={posts}
                showMoreCount={6}
                cardOrientation={
                  CARD_ORIENTATION.HORIZONTAL
                }
                imgPosition={IMG_POSITION.LEFT}
                customClassName={
                  "related-articles-card-list"
                }
                imgVariant={IMG_VARIANTS.ALL_ARTICLES}
                sectionId={SECTION_IDS.RELATED_ARTICLES}
                source={EVENT_SOURCE.ARTICLE}
                flow={RELATED_ARTICLES_FLOW}
              />
            </div>
            {popularCategoriesList?.length > 0 && (
              <PopularCategories
                title={POPULAR_CATEGORIES_TITLE}
                popularCategories={popularCategoriesList}
                sectionId={SECTION_IDS.POPULAR_CATEGORIES}
                source={EVENT_SOURCE.ARTICLE}
                flow={POPULAR_CATEGORIES_FLOW}
              />
            )}
          </div>
        ) : null}
      </Layout>
      <script type="application/ld+json">
        {JSON.stringify(postSeoSchemas)}
      </script>
    </>
  );
};

export default Article;

export const pageQuery = graphql`
  query ArticleQuery(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
    $categoryId: String
    $previousPostId: String
    $nextPostId: String
  ) {
    # selecting the current post by id
    post: wpPost(id: { eq: $id }) {
      id
      excerpt
      content
      title
      uri
      slug
      date(formatString: "DD MMM YYYY")
      tags {
        nodes {
          id
          name
          slug
          uri
        }
      }
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid_tracedSVG
              }
            }
          }
        }
      }
      author {
        node {
          name
          firstName
          lastName
        }
      }
      categories {
        nodes {
          id
          name
          uri
          slug
        }
      }
      seo {
        title
        readingTime
        metaRobotsNofollow
        schema {
          articleType
          pageType
          raw
        }
        canonical
        metaRobotsNoindex
        opengraphAuthor
        opengraphTitle
        opengraphDescription
      }
    }
    allPosts: allWpPost(
      filter: {
        categories: {
          nodes: { elemMatch: { id: { eq: $categoryId } } }
        }
      }
      sort: { fields: [date], order: DESC }
    ) {
      nodes {
        excerpt
        uri
        slug
        date(formatString: "DD MMM YYYY")
        title
        excerpt
        author {
          node {
            id
            name
          }
        }
        seo {
          readingTime
        }
        featuredImage {
          node {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid_tracedSVG
                }
              }
            }
          }
        }
        categories {
          nodes {
            id
            name
            uri
          }
        }
      }
    }

    popularCategories: allWpCategory(
      filter: { slug: { eq: "popular-categories" } }
    ) {
      nodes {
        id
        name
        slug
        uri
        wpChildren {
          nodes {
            id
            name
            slug
            uri
          }
        }
      }
    }

    # this gets us the previous post by id (if it exists)
    previous: wpPost(id: { eq: $previousPostId }) {
      uri
      title
    }

    # this gets us the next post by id (if it exists)
    next: wpPost(id: { eq: $nextPostId }) {
      uri
      title
    }
  }
`;
